import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { Rocket, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import WarmupProgress from '@/components/onboarding/WarmupProgress'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { SyncStatus } from '@/types/shared'
import { FALLBACK_BRAND } from '@/lib/brand'

interface GoLiveBannerProps {
  /** The property's backend-served canonical path. */
  propertyBasePath: string
}

export default function GoLiveBanner({ propertyBasePath }: GoLiveBannerProps) {
  const { props } = usePage<{ syncStatus?: SyncStatus | null; brand?: string; dryRun?: boolean }>()
  const brand =
    typeof props.brand === 'string' && props.brand.trim() !== '' ? props.brand : FALLBACK_BRAND
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pushing, setPushing] = useState(false)

  // Hide once the property is LIVE — the live state has its own GoLiveControl surface.
  // We gate on live/paused STATE, not on syncStatus.lastPush: a paused property runs the
  // push pipeline in dry-run and stamps a fictional lastPush, which used to hide this banner
  // (and its Go Live CTA) on every paused, newly-onboarded property. While paused the
  // warming/ready states below still render. dryRun is only ever absent in legacy/edge
  // payloads; treat missing as paused (show the banner) so we never swallow the go-live path.
  if (props.dryRun === false) return null

  // Warming up: rates haven't been computed yet, so there is nothing to review
  // and nothing meaningful to push. Live progress card — advances via the
  // PropertyNotificationsChannel events re-dispatched on window, and flips to the
  // ready state below once the silent reload delivers a lastComputation.
  if (!props.syncStatus?.lastComputation) {
    return <WarmupProgress brand={brand} syncStatus={props.syncStatus ?? null} />
  }

  function handleGoLive() {
    setPushing(true)
    router.post(`${propertyBasePath}/onboarding/push`, {}, {
      onFinish: () => setPushing(false),
    })
  }

  return (
    <>
      <div className="animate-slide-in rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-emerald-50/80 to-teal-50 p-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100">
              <Rocket className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-emerald-900">
                Preview rates ready
              </h3>
              <p className="mt-0.5 text-sm text-emerald-700">
                These rates are previews — they{"'"}ll keep refining as booking data comes in.
                Review the calendar, tune your Pricing Rules, then press Go Live when you{"'"}re
                ready for {brand} to start pushing rates to your PMS.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setConfirmOpen(true)}
            className="shrink-0 bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus-visible:ring-emerald-500"
          >
            <Rocket className="mr-2 h-4 w-4" />
            Go Live — Start Managing Rates
          </Button>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Go Live with {brand}?</DialogTitle>
            <DialogDescription>
              This will push your computed rates to your PMS and enable automated rate management,
              updating every hour. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={pushing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGoLive}
              disabled={pushing}
              className="bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-500"
            >
              {pushing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Pushing Rates...
                </>
              ) : (
                <>
                  <Rocket className="mr-2 h-4 w-4" />
                  Go Live
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
